'use client';

/**
 * DiscoverBcTwoWay — the killer mechanic.
 *
 * Two ways to capture lifted from Part 4 §14.3: "Save contact" lets
 * the visitor keep your details. "Stay in Touch" pulls THEIR details
 * into your customer database. Best when both are enabled.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiArrowDownTray, HiUserPlus } from 'react-icons/hi2';

type Props = { lang: Locale };

const DiscoverBcTwoWay = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const label = t('DiscoverBc/TwoWay/badge');
  const headlineA = t('DiscoverBc/TwoWay/title1');
  const headlineB = t('DiscoverBc/TwoWay/title2');
  const sub = t('DiscoverBc/TwoWay/subtitle');

  return (
    <section
      id="two-way"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {sub}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <article
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(167,139,250,0.06)',
              border: '1px solid rgba(167,139,250,0.30)',
            }}
          >
            <div
              className="inline-flex items-center justify-center rounded-lg mb-4"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(167,139,250,0.12)',
                border: '1px solid rgba(167,139,250,0.30)',
              }}
            >
              <HiArrowDownTray style={{ width: 20, height: 20, color: '#A78BFA' }} />
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#A78BFA',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              {t('DiscoverBc/TwoWay/save_label')}
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                color: '#F5F7FA',
                margin: '0 0 10px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('DiscoverBc/TwoWay/save_title')}
            </h3>
            <p
              style={{
                color: '#B4C1D8',
                fontSize: 14,
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {t('DiscoverBc/TwoWay/save_body')}
            </p>
          </article>

          <article
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(34,211,238,0.06)',
              border: '1px solid rgba(34,211,238,0.30)',
            }}
          >
            <div
              className="inline-flex items-center justify-center rounded-lg mb-4"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(34,211,238,0.12)',
                border: '1px solid rgba(34,211,238,0.30)',
              }}
            >
              <HiUserPlus style={{ width: 20, height: 20, color: '#22D3EE' }} />
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#22D3EE',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              {t('DiscoverBc/TwoWay/stay_label')}
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                color: '#F5F7FA',
                margin: '0 0 10px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('DiscoverBc/TwoWay/stay_title')}
            </h3>
            <p
              style={{
                color: '#B4C1D8',
                fontSize: 14,
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              {t('DiscoverBc/TwoWay/stay_body')}
            </p>
          </article>
        </div>

        <div
          className="mt-8 rounded-2xl p-6 lg:p-7"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              color: '#B4C1D8',
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            {t('DiscoverBc/TwoWay/why_both_label')}
          </div>
          <p
            style={{
              color: '#B4C1D8',
              fontSize: 15,
              lineHeight: 1.55,
              margin: 0,
              maxWidth: 820,
            }}
          >
            {t('DiscoverBc/TwoWay/why_both_body')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default DiscoverBcTwoWay;
